import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, BadgeCheck, ShieldCheck, Award } from "lucide-react";
import { useRef } from "react";
import { SITE } from "@/lib/site";

const HERO_CAR =
    "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?auto=format&fit=crop&w=1920&q=85";

export default function Hero() {
    const ref = useRef(null);
    const { scrollY } = useScroll();
    const yBg = useTransform(scrollY, [0, 800], [0, 120]);
    const scaleBg = useTransform(scrollY, [0, 800], [1, 1.08]);
    const opacity = useTransform(scrollY, [0, 500], [1, 0.45]);

    return (
        <section
            ref={ref}
            id="hero"
            className="relative min-h-screen w-full overflow-hidden bg-bg"
            data-testid="hero-section"
        >
            {/* Mercedes background image — full bleed */}
            <motion.div
                style={{ y: yBg, scale: scaleBg, opacity }}
                className="absolute inset-0"
            >
                <img
                    src={HERO_CAR}
                    alt="Луксозен Mercedes-Benz на пътя"
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                    data-testid="hero-car-image"
                />
                {/* Cinematic gold lens flare sweep */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="lens-flare" />
                </div>
            </motion.div>

            {/* Readability overlays — soft cream wash keeps the photo airy & bright like konstantin-mobile.bg */}
            {/* Warm champagne veil over the whole photo */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(90deg, rgba(239,230,208,0.86) 0%, rgba(239,230,208,0.55) 40%, rgba(239,230,208,0.18) 75%, rgba(239,230,208,0.05) 100%)",
                }}
            />
            {/* Bottom fade for scroll indicator + card transition */}
            <div
                className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, transparent 0%, rgba(239,230,208,0.55) 60%, rgba(239,230,208,0.98) 100%)",
                }}
            />
            {/* Subtle film grain */}
            <div className="absolute inset-0 grain pointer-events-none opacity-30" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-24 min-h-screen flex items-center">
                <div className="max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/50 bg-champagne/60 backdrop-blur-sm text-gold text-sm sm:text-[15px] uppercase tracking-[0.24em] mb-8"
                        data-testid="hero-badge"
                    >
                        <Award size={16} /> 700+ продадени автомобила за три години
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight text-ink"
                        data-testid="hero-headline"
                    >
                        Най-добрите автомобили <br />
                        <span className="text-gradient-gold">за най-добрите клиенти!</span>
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.6 }}
                        className="mt-12 flex flex-col sm:flex-row gap-4"
                    >
                        <a
                            href="/cars"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-dark text-champagne font-medium px-8 py-4 rounded-full transition-all duration-300 shadow-gold"
                        >
                            Разгледай автомобилите
                            <ArrowRight
                                size={18}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </a>
                        <a
                            href="#contact"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center justify-center gap-3 border border-gold/80 bg-champagne/50 backdrop-blur-sm hover:bg-gold/15 text-gold px-8 py-4 rounded-full transition-all duration-300"
                        >
                            Свържи се с нас
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1 }}
                        className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-ink-muted"
                    >
                        <div className="flex items-center gap-2">
                            <BadgeCheck size={18} className="text-gold" />
                            Проверена история
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={18} className="text-gold" />
                            Гаранция на коректността
                        </div>
                        <div className="flex items-center gap-2">
                            <Award size={18} className="text-gold" />
                            700+ доволни клиенти
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll indicator */}
            <motion.a
                href="#inventory"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.6 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 text-ink-muted hover:text-gold transition-colors group"
                data-testid="hero-scroll-indicator"
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    className="w-px h-12 bg-gradient-to-b from-gold to-transparent"
                />
                <ChevronDown size={16} className="text-gold group-hover:text-gold-dark" />
            </motion.a>
        </section>
    );
}
